import { isInvoiceFamilyDocumentType } from './documentNumberFormat';

export type PaymentTermsMode = 'net_days' | 'immediate';
export type PaymentTermsContext = 'offer' | 'invoice';

export const IMMEDIATE_DUE_WORKING_DAYS = 3;

export type PaymentTermsSentenceParams = {
  mode?: PaymentTermsMode | unknown;
  days: unknown;
  context?: PaymentTermsContext;
  skontoPercent?: unknown;
  skontoDays?: unknown;
};

export const DEFAULT_SKONTO_DAYS = 7;

function parseYmdUtc(ymd: string): Date | null {
  const s = String(ymd ?? '').slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!match) return null;
  const dt = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function formatYmdUtc(dt: Date): string {
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

export function normalizePaymentTermsMode(raw: unknown): PaymentTermsMode {
  return raw === 'immediate' ? 'immediate' : 'net_days';
}

export function normalizePaymentTermsDays(raw: unknown): number {
  if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 1) return Math.min(9999, Math.floor(raw));
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = parseInt(raw.trim(), 10);
    if (Number.isFinite(n) && n >= 1) return Math.min(9999, n);
  }
  return 7;
}

export function paymentTermsContextFromDocumentType(label: string): PaymentTermsContext {
  return isInvoiceFamilyDocumentType(label) ? 'invoice' : 'offer';
}

export function formatSkontoClause(percent: unknown, days?: unknown): string {
  if (percent == null || percent === '') return '';
  const n = typeof percent === 'number' ? percent : parseFloat(String(percent).replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return '';
  const rounded = Math.round(n * 100) / 100;
  const rawDays =
    days == null || days === ''
      ? NaN
      : typeof days === 'number'
        ? days
        : parseInt(String(days), 10);
  const skDays =
    Number.isFinite(rawDays) && rawDays >= 1 ? Math.min(9999, Math.floor(rawDays)) : DEFAULT_SKONTO_DAYS;
  return ` Bei Zahlung innerhalb von ${skDays} ${skDays === 1 ? 'Tag' : 'Tagen'} gew\u00e4hren wir ${rounded}\u00a0% Skonto.`;
}

export function formatPaymentTermsSentence(params: PaymentTermsSentenceParams): string {
  const mode = normalizePaymentTermsMode(params.mode);
  if (mode === 'immediate') {
    return params.context === 'offer'
      ? 'Zahlbar sofort ohne Abzug ab Rechnungsdatum.'
      : 'Zahlbar sofort ohne Abzug.';
  }
  const d = normalizePaymentTermsDays(params.days);
  const base = `Die Zahlungsfrist nach Rechnungsstellung betr\u00e4gt ${d} ${d === 1 ? 'Tag' : 'Tage'}.`;
  return `${base}${formatSkontoClause(params.skontoPercent, params.skontoDays)}`;
}

const SKONTO_SUFFIX_RE =
  '(?: Bei Zahlung innerhalb von \\d+ (?:Tag|Tagen) gew\u00e4hren wir .+ % Skonto\\.)?';
const NET_DAYS_SENTENCE_RE = new RegExp(
  `^Die Zahlungsfrist nach Rechnungsstellung betr\u00e4gt \\d+ Tage?\\.${SKONTO_SUFFIX_RE}$`,
  'u',
);
const SOFORT_SENTENCE_RE = new RegExp(
  `^Zahlbar sofort ohne Abzug(?: ab Rechnungsdatum)?\\.${SKONTO_SUFFIX_RE}$`,
  'u',
);

/** True for empty or auto-generated Zahlungsziel lines — not for user-edited paragraphs. */
export function isGeneratedPaymentTermsSentence(text: string): boolean {
  const t = String(text ?? '').trim().replace(/\u00a0/g, ' ');
  if (!t) return true;
  return SOFORT_SENTENCE_RE.test(t) || NET_DAYS_SENTENCE_RE.test(t);
}

/**
 * Editor fallback before company/org settings are applied: empty or the bare
 * 7-Tage sentence without Skonto. User-edited days (e.g. 7 → 14) are not a placeholder.
 */
export function isPaymentTermsPlaceholderSentence(
  text: string,
  context: PaymentTermsContext = 'offer',
): boolean {
  const t = String(text ?? '').trim().replace(/\u00a0/g, ' ');
  if (!t) return true;
  const fallback = formatPaymentTermsSentence({
    mode: 'net_days',
    days: 7,
    context,
  }).replace(/\u00a0/g, ' ');
  return t === fallback;
}

function normalizePaymentTermsCompare(text: string): string {
  return String(text ?? '').trim().replace(/\u00a0/g, ' ');
}

/**
 * True when the paragraph is still an auto default and may be replaced:
 * empty, the 7-day placeholder, or the current company sentence.
 * User-edited days (e.g. 7 → 14) stay locked.
 */
export function isReplaceableDefaultPaymentTermsSentence(
  text: string,
  context: PaymentTermsContext = 'offer',
  companyLine?: string | null,
): boolean {
  if (isPaymentTermsPlaceholderSentence(text, context)) return true;
  const company = normalizePaymentTermsCompare(companyLine ?? '');
  return Boolean(company) && normalizePaymentTermsCompare(text) === company;
}

/** YYYY-MM-DD + calendar days → YYYY-MM-DD (UTC date arithmetic). */
export function addCalendarDaysYmd(ymd: string, days: number): string {
  const dt = parseYmdUtc(ymd);
  if (!dt) return String(ymd ?? '').slice(0, 10);
  dt.setUTCDate(dt.getUTCDate() + Math.floor(days));
  return formatYmdUtc(dt);
}

/**
 * Advance `count` Werktage (Mon–Sat) after `ymd`, skipping Sundays and
 * `holidayDates` (YYYY-MM-DD). The issue date itself is not counted.
 */
export function addWorkingDaysYmd(ymd: string, count: number, holidayDates: Set<string>): string {
  const dt = parseYmdUtc(ymd);
  if (!dt) return String(ymd ?? '').slice(0, 10);
  const n = Math.max(0, Math.floor(count));
  let added = 0;
  let guard = 0;
  while (added < n && guard < 366) {
    dt.setUTCDate(dt.getUTCDate() + 1);
    guard += 1;
    const key = formatYmdUtc(dt);
    const dow = dt.getUTCDay();
    if (dow === 0) continue;
    if (holidayDates.has(key)) continue;
    added += 1;
  }
  return formatYmdUtc(dt);
}

export function resolveInvoiceDueYmd(opts: {
  issueYmd: string;
  mode?: PaymentTermsMode | unknown;
  days?: unknown;
  holidayDates?: Set<string> | Iterable<string>;
}): string {
  const mode = normalizePaymentTermsMode(opts.mode);
  if (mode === 'immediate') {
    const holidays =
      opts.holidayDates instanceof Set ? opts.holidayDates : new Set(opts.holidayDates ?? []);
    return addWorkingDaysYmd(opts.issueYmd, IMMEDIATE_DUE_WORKING_DAYS, holidays);
  }
  return addCalendarDaysYmd(opts.issueYmd, normalizePaymentTermsDays(opts.days));
}
